/**
 * Omni Flash Direct — dead-simple video edit recipe (slug: omni-flash-direct).
 * Upload ONE video + type ONE prompt → whole clip goes straight to
 * gemini-omni-flash-preview in a single call. No cut detection, no phases.
 * Follow-up prompts iterate on the result (multi-turn).
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Upload, Wand2, Download, ArrowLeft, Loader2, Zap,
  AlertTriangle, Send, Clock, Film,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const LS_KEY = "omni_flash_direct_job_id";

const C = {
  bg: "#14181C", panel: "#1B212A", panel2: "#212934", line: "#2B333E",
  text: "#E8EDF2", dim: "#8A97A5", accent: "#22D3EE", amber: "#F59E0B",
  red: "#F87171", green: "#34D399",
};

export default function OmniFlashDirectPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [recent, setRecent] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadJob = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/omni-flash-direct/${id}`);
      setJob(data);
      if (data.status === "failed") setError(data.error || "Edit failed");
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) loadJob(saved);
    axios.get(`${API_BASE}/api/omni-flash-direct/recent`)
      .then(r => setRecent(r.data?.jobs || [])).catch(() => {});
  }, [loadJob]);

  useEffect(() => {
    if (job?.status === "processing") {
      pollRef.current = setInterval(() => loadJob(job.id), 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.id, job?.status, loadJob]);

  const start = async () => {
    if (!file || !prompt.trim()) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("video", file);
      fd.append("prompt", prompt.trim());
      const { data } = await axios.post(`${API_BASE}/api/omni-flash-direct/start`, fd);
      setJob(data);
      localStorage.setItem(LS_KEY, data.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed — please retry");
    } finally { setBusy(false); }
  };

  const iterate = async () => {
    if (!followUp.trim() || !job) return;
    setBusy(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/omni-flash-direct/${job.id}/iterate`, { prompt: followUp.trim() });
      setFollowUp("");
      loadJob(job.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Follow-up failed — please retry");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setJob(null); setFile(null); setPrompt(""); setError("");
    localStorage.removeItem(LS_KEY);
  };

  const processing = job?.status === "processing";
  const done = job?.status === "done";

  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="omni-flash-direct-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ borderColor: C.line }}>
        <button onClick={() => navigate("/recipes")} className="p-2 rounded-lg hover:bg-white/5" data-testid="omni-direct-back-btn">
          <ArrowLeft className="w-4 h-4" style={{ color: C.dim }} />
        </button>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(34,211,238,0.12)" }}>
          <Zap className="w-4 h-4" style={{ color: C.accent }} />
        </div>
        <div>
          <h1 className="text-sm font-bold tracking-wide">Omni Flash Direct</h1>
          <p className="text-[11px]" style={{ color: C.dim }}>One video · one prompt · one Gemini Omni Flash pass — no cuts, no phases</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-xs" style={{ background: "rgba(248,113,113,0.08)", border: `1px solid ${C.red}40`, color: C.red }} data-testid="omni-direct-error">
            <AlertTriangle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {!job && (
          <div className="rounded-xl p-6 space-y-5" style={{ background: C.panel, border: `1px solid ${C.line}` }}>
            <div
              onClick={() => fileRef.current?.click()}
              className="rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors hover:border-cyan-400/40"
              style={{ borderColor: file ? `${C.accent}66` : C.line }}
              data-testid="omni-direct-dropzone"
            >
              <input ref={fileRef} type="file" accept="video/*" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="omni-direct-file-input" />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Film className="w-4 h-4" style={{ color: C.accent }} />
                  <span>{file.name}</span>
                  <span style={{ color: C.dim }}>({(file.size / 1048576).toFixed(1)} MB)</span>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: C.dim }} />
                  <p className="text-sm">Drop or click to upload a video</p>
                  <p className="text-[11px] mt-1" style={{ color: C.dim }}>MP4 / MOV — Omni Flash preview outputs up to ~10s</p>
                </>
              )}
            </div>

            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="Describe the edit — e.g. 'Make it golden hour, warm rim light, keep everything else identical'"
              className="w-full rounded-lg p-3 text-sm outline-none resize-none"
              style={{ background: C.panel2, border: `1px solid ${C.line}`, color: C.text }}
              data-testid="omni-direct-prompt-input"
            />

            <button
              onClick={start}
              disabled={!file || !prompt.trim() || busy}
              className="w-full py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-40 hover:brightness-110"
              style={{ background: C.accent, color: "#06232A" }}
              data-testid="omni-direct-start-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Send to Omni Flash
            </button>
          </div>
        )}

        {job && (
          <div className="rounded-xl p-6 space-y-4" style={{ background: C.panel, border: `1px solid ${C.line}` }} data-testid="omni-direct-job-panel">
            {job.exceeds_max_duration && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-[11px]" style={{ background: "rgba(245,158,11,0.08)", border: `1px solid ${C.amber}40`, color: C.amber }} data-testid="omni-direct-duration-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Source is {Math.round(job.duration)}s — Omni Flash preview caps output at ~10s, the result may be trimmed.
              </div>
            )}

            {processing && (
              <div className="text-center py-10" data-testid="omni-direct-processing">
                <Loader2 className="w-8 h-8 mx-auto animate-spin mb-3" style={{ color: C.accent }} />
                <p className="text-sm font-medium">{job.last_message || "Editing with Gemini Omni Flash…"}</p>
                <p className="text-[11px] mt-1" style={{ color: C.dim }}>Single-pass edits usually take 1–3 minutes</p>
              </div>
            )}

            {done && job.result_url && (
              <>
                <video key={job.result_url} src={job.result_url} controls className="w-full rounded-lg" style={{ maxHeight: 480, background: "#000" }} data-testid="omni-direct-result-video" />
                <div className="flex gap-2">
                  <a href={job.result_url} download target="_blank" rel="noreferrer"
                    className="flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2"
                    style={{ background: C.panel2, border: `1px solid ${C.line}` }} data-testid="omni-direct-download-btn">
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                  <button onClick={reset} className="px-4 py-2.5 rounded-lg text-xs font-bold" style={{ background: C.panel2, border: `1px solid ${C.line}`, color: C.dim }} data-testid="omni-direct-new-btn">
                    New edit
                  </button>
                </div>

                {/* Multi-turn follow-up */}
                <div className="pt-3 space-y-2" style={{ borderTop: `1px solid ${C.line}` }}>
                  <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.dim }}>Follow-up edit (same video)</p>
                  <div className="flex gap-2">
                    <input
                      value={followUp}
                      onChange={(e) => setFollowUp(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && iterate()}
                      placeholder="e.g. 'Now make the sky slightly more dramatic'"
                      className="flex-1 rounded-lg px-3 py-2.5 text-sm outline-none"
                      style={{ background: C.panel2, border: `1px solid ${C.line}`, color: C.text }}
                      data-testid="omni-direct-iterate-input"
                    />
                    <button onClick={iterate} disabled={!followUp.trim() || busy}
                      className="px-4 rounded-lg disabled:opacity-40" style={{ background: C.accent, color: "#06232A" }}
                      data-testid="omni-direct-iterate-btn">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {(job.iterations || []).length > 1 && (
                  <div className="space-y-1.5 pt-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.dim }}>Turns</p>
                    {job.iterations.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg" style={{ background: C.panel2 }}>
                        <span className="font-bold shrink-0" style={{ color: C.accent }}>#{i + 1}</span>
                        <span className="truncate flex-1" style={{ color: C.dim }}>{it.prompt}</span>
                        <a href={it.result_url} target="_blank" rel="noreferrer" className="shrink-0 underline" style={{ color: C.accent }}>view</a>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {job.status === "failed" && (
              <div className="text-center py-6" data-testid="omni-direct-failed">
                <p className="text-sm" style={{ color: C.red }}>{job.error || "Edit failed"}</p>
                <button onClick={reset} className="mt-3 px-4 py-2 rounded-lg text-xs font-bold" style={{ background: C.panel2, border: `1px solid ${C.line}` }} data-testid="omni-direct-retry-btn">
                  Start over
                </button>
              </div>
            )}
          </div>
        )}

        {/* Recent runs */}
        {recent.length > 0 && !job && (
          <div className="rounded-xl p-5" style={{ background: C.panel, border: `1px solid ${C.line}` }}>
            <p className="text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: C.dim }}>
              <Clock className="w-3 h-3" /> Recent runs
            </p>
            <div className="space-y-1.5">
              {recent.slice(0, 6).map((r) => (
                <button key={r.id} onClick={() => { setJob(r); localStorage.setItem(LS_KEY, r.id); }}
                  className="w-full flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg text-left hover:bg-white/5"
                  style={{ background: C.panel2 }} data-testid={`omni-direct-recent-${r.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.status === "done" ? C.green : r.status === "failed" ? C.red : C.amber }} />
                  <span className="truncate flex-1" style={{ color: C.dim }}>{r.prompt}</span>
                  <span className="shrink-0" style={{ color: C.dim }}>{(r.created_at || "").slice(0, 10)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
